import { shouldUseAnchorForLink } from "@faire/web/ui/lib";
import { Anchor, Link } from "@faire/web/ui/LinkAnchor";
import { useHistory } from "react-router-dom";

import { sanitizeLink } from "./sanitizeLink";
import { IButtonWebProps } from "./types";

type ButtonAttributes = {
  as: "button";
  linkProps: Record<string, never>;
};

type AnchorAttributes = {
  as: typeof Anchor;
  linkProps: { href: string };
};

type LinkAttributes = {
  as: typeof Link;
  linkProps: {
    to: IButtonWebProps["linkTo"];
    replace: IButtonWebProps["replace"];
  };
};

type PolimorphicAttributes =
  | ButtonAttributes
  | AnchorAttributes
  | LinkAttributes;

// Copied from core/web/src/slate/Button/utils
/**
 * Returns the appropriate attributes for the button based on the linkTo and other anchor props.
 *
 * If no linkTo or other anchor props are provided, this hook return `as="button"`.
 *
 * If the link has to be external, this hook returns `as={Anchor}` with the href set.
 *
 * Otherwise, this hook returns `as={Link}` with the `to` and `replace` set.
 *
 * @param props - The button props.
 * @returns The appropriate attributes for the button component.
 */
export const usePolimorphicAttributes = (
  props: IButtonWebProps
): PolimorphicAttributes => {
  const { linkTo } = props;
  const history = useHistory();

  if (linkTo === undefined) {
    return { as: "button", linkProps: {} };
  }

  if ("download" in props || shouldUseAnchorForLink(linkTo)) {
    const descriptor =
      typeof linkTo === "function" ? linkTo(history.location) : linkTo;

    const resolved =
      typeof descriptor === "string"
        ? sanitizeLink(descriptor, props.target)
        : history.createHref(descriptor);
    return {
      as: Anchor,
      linkProps: { href: resolved },
    };
  }

  return {
    as: Link,
    linkProps: {
      to: sanitizeLink(linkTo, props.target),
      replace: props.replace,
    },
  };
};
